import { MultiSelect } from 'primeng/multiselect';

/**
 * Performance patch for PrimeNG MultiSelect selection checks.
 *
 * Problem (profiled): opening a multiselect that has thousands of options with
 * (near-)everything selected freezes the page for tens of seconds. The header
 * "select all" checkbox evaluates `allSelected()`, which iterates every option
 * and calls `isSelected(option)` for each. `isSelected` itself does a linear
 * `.some(...)` over the selected model, and because the campaign options carry
 * no `dataKey`/scalar `optionValue` equalityKey, every comparison falls through
 * to the (expensive, per-call) `deepEquals`. With ~15k options all selected that
 * is ~15k x 15k ≈ 225M deepEquals calls in a single synchronous change-detection
 * pass triggered from the click handler's `cd.detectChanges()`.
 *
 * Fix: when there is no custom equality key (i.e. comparisons would be value
 * based) and the model is large, memoise the selected values in a `Set` keyed by
 * the array reference and answer `isSelected` via O(1) `Set.has`. This turns
 * `allSelected()` from O(n*m) into O(n). Behaviour is identical to the original
 * value comparison for primitive option values; any case the Set cannot answer
 * safely (object values without a key, or a custom equalityKey) is delegated to
 * the original implementation.
 *
 * Patching the prototype (rather than the m-multiselect wrapper) is deliberate:
 * the wrapper renders an inner `<p-multiselect>` instance, so wrapper/ViewChild
 * overrides never reach the instance the user actually interacts with.
 */
const FAST_PATH_MIN_MODEL = 50;

interface SelectionCache {
  __selModelRef?: unknown;
  __selSet?: Set<unknown>;
}

export function applyMultiSelectSelectionPatch(): void {
  const proto = MultiSelect.prototype as unknown as {
    isSelected(option: unknown): boolean;
    getOptionValue(option: unknown): unknown;
    equalityKey(): string | null;
    modelValue(): unknown[] | null;
    __selectionPatchApplied?: boolean;
  };

  if (proto.__selectionPatchApplied) {
    return;
  }
  proto.__selectionPatchApplied = true;

  const original = proto.isSelected;

  proto.isSelected = function (
    this: SelectionCache & typeof proto,
    option: unknown
  ) {
    const model = this.modelValue() || [];
    const key = this.equalityKey();

    // Only safe to short-circuit when comparisons are pure value equality.
    if (!key && model.length > FAST_PATH_MIN_MODEL) {
      const optionValue = this.getOptionValue(option);
      // Sets compare objects by reference, not deepEquals — restrict to scalars.
      if (optionValue === null || typeof optionValue !== 'object') {
        if (this.__selModelRef !== model) {
          this.__selModelRef = model;
          this.__selSet = new Set(model);
        }
        return this.__selSet?.has(optionValue) ?? false;
      }
    }

    return original.call(this, option);
  };
}

applyMultiSelectSelectionPatch();
